import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Breadcrumb from "@/components/video/Breadcrumb";
import VideoCard from "@/components/video/VideoCard";
import { videos, regionOptions, yearOptions, videoTypes, type VideoItem } from "@/mocks/videos";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

type SortKey = "latest" | "hot";

const PAGE_SIZE = 9;

const categoryMeta: Record<string, { title: string; desc: string }> = {
  电影: { title: "电影列表", desc: "汇集科幻、悬疑、动作、剧情等题材的高清电影资源，支持按年份与地区筛选，按最新发布或热门程度查看。" },
  电视剧: { title: "电视剧列表", desc: "都市、悬疑、家庭、青春等题材的连续剧集，支持按年份与地区筛选，按最新发布或热门程度查看。" },
  短视频: { title: "短视频列表", desc: "美食、旅行、萌宠、生活技巧等短视频内容，短小精悍，随时开刷。" },
};

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const validType = type && (videoTypes as string[]).includes(type) ? type : null;
  const meta = validType ? categoryMeta[validType] : null;

  const [year, setYear] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useDocumentTitle(
    `${meta ? meta.title : "视频分类"} - 黄瓜视频App官方网站`,
    meta ? meta.desc : "黄瓜视频App官方网站视频分类页，按年份与地区筛选视频资源。",
    `黄瓜视频App,${validType ?? "视频分类"},视频列表,高清视频`,
  );

  const list = useMemo<VideoItem[]>(() => {
    if (!validType) return [];
    return videos.filter((v) => v.type === validType);
  }, [validType]);

  const filtered = useMemo(() => {
    const result = list.filter((v) => {
      if (year && String(v.year) !== year) return false;
      if (region && v.region !== region) return false;
      return true;
    });
    result.sort((a, b) => {
      if (sort === "hot") return b.hot - a.hot;
      return a.publishDate < b.publishDate ? 1 : -1;
    });
    return result;
  }, [list, year, region, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const applyFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const reset = () => {
    setYear("");
    setRegion("");
    setSort("latest");
    setPage(1);
  };

  const filterControls = (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-sm text-foreground-500 mb-3">年份</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => applyFilter(() => setYear(""))}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
              year === "" ? "bg-primary-500 text-background-950" : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
            }`}
          >
            全部
          </button>
          {yearOptions.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => applyFilter(() => setYear(String(y)))}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                year === String(y) ? "bg-primary-500 text-background-950" : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm text-foreground-500 mb-3">地区</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => applyFilter(() => setRegion(""))}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
              region === "" ? "bg-accent-500 text-background-950" : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
            }`}
          >
            全部
          </button>
          {regionOptions.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => applyFilter(() => setRegion(r))}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                region === r ? "bg-accent-500 text-background-950" : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (!validType) {
    return (
      <div className="w-full px-4 md:px-8 pt-28 md:pt-36 pb-24 text-center">
        <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
        <p className="mt-3 text-foreground-500">没有找到对应的视频分类，返回首页继续浏览吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full pt-20 md:pt-24">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 border-b border-background-200/60">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta!.title }]} />
        <h1 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-foreground-950">{meta!.title}</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-400 max-w-3xl leading-relaxed">{meta!.desc}</p>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block lg:w-64 shrink-0">
            <div className="p-5 rounded-lg bg-background-100 border border-background-200/60 sticky top-24">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading font-semibold text-foreground-950">筛选</h2>
                <button type="button" onClick={reset} className="text-xs text-primary-400 hover:text-primary-300 cursor-pointer">
                  清空
                </button>
              </div>
              {filterControls}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                className="lg:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center" />
                筛选
              </button>

              <div className="flex items-center gap-2 ml-auto">
                {(["latest", "hot"] as SortKey[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyFilter(() => setSort(key))}
                    className={`px-4 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === key ? "bg-primary-500 text-background-950" : "bg-background-200/70 text-foreground-700 hover:bg-background-300"
                    }`}
                  >
                    {key === "latest" ? "最新发布" : "热门程度"}
                  </button>
                ))}
              </div>
            </div>

            {filterOpen && (
              <div className="lg:hidden mb-6 p-5 rounded-lg bg-background-100 border border-background-200/60">
                <div className="flex items-center justify-between mb-5">
                  <span className="font-heading font-semibold text-foreground-950">筛选条件</span>
                  <button type="button" onClick={reset} className="text-xs text-primary-400 cursor-pointer">
                    清空
                  </button>
                </div>
                {filterControls}
              </div>
            )}

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center bg-background-100 rounded-lg border border-background-200/60">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">没有匹配的视频</h3>
                <p className="mt-2 text-sm text-foreground-500">当前筛选条件下没有找到内容，试试重置筛选条件。</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
                  重置筛选
                </button>
              </div>
            )}

            {totalPages > 1 && pageItems.length > 0 && (
              <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-md flex items-center justify-center bg-background-100 border border-background-200/70 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-500 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-md text-sm font-label transition-colors cursor-pointer ${
                      currentPage === i + 1
                        ? "bg-primary-500 text-background-950"
                        : "bg-background-100 border border-background-200/70 text-foreground-700 hover:border-primary-500"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-md flex items-center justify-center bg-background-100 border border-background-200/70 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-500 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}